import {
  connectBaseApi,
  userClient,
  adminClient,
  createRpcMutation,
  createRpcQuery,
} from "@racoon/api";
import { ConnectProto } from "@racoon/api";

const userGrpc = connectBaseApi.injectEndpoints({
  endpoints: (build) => ({
    createUser: createRpcMutation(build, userClient, "createUser", {
      invalidatesTags: [{ type: "Employee", id: "LIST" }],
    }),
    updateUser: createRpcMutation(build, userClient, "updateUser", {
      // The edited employee by id as well as the list: getUser caches per id, and
      // a card reopened after a save would otherwise serve the pre-save record.
      invalidatesTags: (_result, _error, arg) => [
        { type: "Employee" as const, id: String(arg.id) },
        { type: "Employee" as const, id: "LIST" },
      ],
    }),
    getUser: createRpcQuery(build, userClient, "getUser", {
      providesTags: (_result, _error, arg) => [{ type: "Employee" as const, id: String(arg.id) }],
    }),
    listUsers: createRpcQuery(build, userClient, "listUsers", {
      providesTags: (result) =>
        result?.users
          ? [
              ...result.users.map((u) => ({ type: "Employee" as const, id: String(u.id) })),
              { type: "Employee" as const, id: "LIST" },
            ]
          : [{ type: "Employee" as const, id: "LIST" }],
    }),
    inviteUser: createRpcMutation(build, userClient, "inviteUser", {
      invalidatesTags: [{ type: "Employee", id: "LIST" }],
    }),
    // A password is not part of the cached User, so nothing needs refetching.
    setUserPassword: createRpcMutation(build, userClient, "setUserPassword"),
    listAllUsers: build.query<ConnectProto.UserList, void>({
      queryFn: async (_arg, _api, _extra, baseQuery) => {
        const res = await baseQuery({ client: adminClient, method: "listUsers", args: {} });
        return res.error ? { error: res.error } : { data: res.data as ConnectProto.UserList };
      },
      providesTags: (result) =>
        result?.users
          ? [
              ...result.users.map((u) => ({ type: "Employee" as const, id: String(u.id) })),
              { type: "Employee" as const, id: "LIST" },
            ]
          : [{ type: "Employee" as const, id: "LIST" }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useCreateUserMutation,
  useUpdateUserMutation,
  useLazyGetUserQuery,
  useListUsersQuery,
  useInviteUserMutation,
  useSetUserPasswordMutation,
  useListAllUsersQuery,
} = userGrpc;

// Type aliases for convenience
export type User = ConnectProto.User;

export const roleMap = {
  USER_ROLE_UNDEFINED: ConnectProto.UserRole.UNDEFINED,
  USER_ROLE_ADMIN: ConnectProto.UserRole.ADMIN,
  USER_ROLE_RECEIVER_USER: ConnectProto.UserRole.RECEIVER_USER,
  USER_ROLE_DRIVER: ConnectProto.UserRole.DRIVER,
  USER_ROLE_PROVIDER_DISPATCHER: ConnectProto.UserRole.PROVIDER_DISPATCHER,
  USER_ROLE_SUPPORT: ConnectProto.UserRole.SUPPORT,
  USER_ROLE_PROVIDER_OWNER: ConnectProto.UserRole.PROVIDER_OWNER,
  USER_ROLE_DISPOSAL_SITE_OWNER: ConnectProto.UserRole.DISPOSAL_SITE_OWNER,
  USER_ROLE_GOVERNMENT_AUDITOR: ConnectProto.UserRole.GOVERNMENT_AUDITOR,
  USER_ROLE_GOVERNMENT_AUDITOR_EDITOR: ConnectProto.UserRole.GOVERNMENT_AUDITOR_EDITOR,
  USER_ROLE_GOVERNMENT_ECO: ConnectProto.UserRole.GOVERNMENT_ECO,
  USER_ROLE_PROVIDER_MECHANIC: ConnectProto.UserRole.PROVIDER_MECHANIC,
  USER_ROLE_PROVIDER_LOADER: ConnectProto.UserRole.PROVIDER_LOADER,
  USER_ROLE_GPS_INNER_SERVICE: ConnectProto.UserRole.GPS_INNER_SERVICE,
  USER_ROLE_PROVIDER_OWNER_VIEW_ONLY: ConnectProto.UserRole.PROVIDER_OWNER_VIEW_ONLY,
  USER_ROLE_PROVIDER_SALES: ConnectProto.UserRole.PROVIDER_SALES,
} satisfies Record<string, ConnectProto.UserRole>;

export type RoleKey = keyof typeof roleMap;

/** Maps a numeric proto enum back to its string role key. */
const reverseRoleMap: Record<number, RoleKey> = Object.fromEntries(
  Object.entries(roleMap).map(([k, v]) => [v, k as RoleKey]),
);

/** Convenience: numeric proto enum from a (possibly empty) string role key. */
export const roleKeyToEnum = (role: RoleKey | "" | undefined): ConnectProto.UserRole | undefined =>
  role ? roleMap[role] : undefined;

/** Convenience: string role key from a numeric proto enum. */
export const roleEnumToKey = (role: ConnectProto.UserRole | number | undefined): RoleKey | "" =>
  role == null ? "" : (reverseRoleMap[role as number] ?? "");
